import { keyframes } from '@emotion/react';
import { Box, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { ConnectionsIcon } from '@/components/icons/ConnectionsIcon';
import { Family01Icon } from '@/components/icons/Family01Icon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { MappingIcon } from '@/components/icons/MappingIcon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

const STEPS = [
  {
    id: 'documents',
    label: 'Reading the documents you uploaded',
    Icon: FileSearch02Icon,
  },
  {
    id: 'members',
    label: 'Identifying household members and their families',
    Icon: Family01Icon,
  },
  {
    id: 'connections',
    label: 'Finding trusts, entities and how they connect',
    Icon: ConnectionsIcon,
  },
  {
    id: 'ownership',
    label: 'Mapping who owns what across the household',
    Icon: MappingIcon,
  },
  {
    id: 'details',
    label: 'Double-checking household details',
    Icon: SearchSmIcon,
  },
] as const;

const stepFadeOutMs = 360;
const stepFadeInMs = 640;
const stepTravelPx = 5;
const inactiveStepScale = 0.98;
const stepGlintPeriodMs = 3600;

// The glint gradient is 2.5x the label width W. The orange band sits at its
// center, so these positions move the band center from -W to 2W: left to
// right across the label, which covers 0 to W.
const stepGlintBackgroundSize = '250% 100%';
const householdStepGlint = keyframes({
  from: { backgroundPosition: '150% 0' },
  to: { backgroundPosition: '-50% 0' },
});

// The icon is an SVG drawn in `currentColor`, so it cannot use the clipped
// text gradient. Instead its color peaks when the band passes over it. The
// icon sits about 22px left of the label, which the band reaches at about 30%
// of each pass for usual label widths. The band's half-width is about 10% of
// the pass, so the color ramps over the same distance.
const householdStepIconGlint = keyframes({
  '0%, 20%, 40%, 100%': { color: COLORS.TEXT.PRIMARY },
  '30%': { color: COLORS.BRAND.ORANGE },
});

/** Runs `glint` on the shared glint clock for the current step. The glint
 * starts after the step fade-in ends. Reduced motion and forced-colors mode
 * turn it off.
 */
function getStepGlintAnimationSx(
  glint: ReturnType<typeof keyframes>,
  isCurrent: boolean
) {
  return {
    animation: isCurrent
      ? `${glint} ${stepGlintPeriodMs}ms linear ${stepFadeOutMs + stepFadeInMs}ms infinite`
      : 'none',
    '@media (prefers-reduced-motion: reduce), (forced-colors: active)': {
      animation: 'none',
    },
  };
}

export type HouseholdLoadingStep = (typeof STEPS)[number]['id'];

export interface HouseholdLoadingStepsProps {
  /** The caller owns timing and persistence. Keep `details` active until
   * processing ends, then unmount. Mounting renders the supplied step directly.
   */
  step: HouseholdLoadingStep;
  /** Display names formatted by the caller, for example "Logan, Caroline". */
  memberNames?: string;
}

/** Presentational progress with one visible step; no clock or fetching. */
export function HouseholdLoadingSteps({
  step,
  memberNames,
}: HouseholdLoadingStepsProps) {
  const activeIndex = STEPS.findIndex((item) => item.id === step);
  const getLabel = (item: (typeof STEPS)[number]) =>
    item.id === 'members' && memberNames
      ? `Identifying ${memberNames} and their family members`
      : item.label;
  // The step union guarantees that this lookup exists.
  const current = STEPS[activeIndex]!;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={visuallyHidden}
      >
        {getLabel(current)}
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          display: 'grid',
          overflow: 'visible',
        }}
      >
        {STEPS.map((item, index) => {
          const isCurrent = index === activeIndex;
          return (
            <Box
              key={item.id}
              sx={{
                gridArea: '1 / 1',
                minWidth: 0,
                opacity: isCurrent ? 1 : 0,
                visibility: isCurrent ? 'visible' : 'hidden',
                color: isCurrent ? COLORS.TEXT.PRIMARY : COLORS.GRAY[400],
                transform: isCurrent
                  ? 'translateY(0) scale(1)'
                  : `translateY(${index < activeIndex ? -stepTravelPx : stepTravelPx}px) scale(${inactiveStepScale})`,
                transformOrigin: 'left center',
                // Finish the outgoing motion before the incoming text appears.
                transition: isCurrent
                  ? `opacity ${stepFadeInMs}ms ease-out ${stepFadeOutMs}ms, transform ${stepFadeInMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${stepFadeOutMs}ms, color ${stepFadeInMs}ms ease-out ${stepFadeOutMs}ms, visibility 0s linear ${stepFadeOutMs}ms`
                  : `opacity ${stepFadeOutMs}ms ease-in, transform ${stepFadeOutMs}ms ease-in, color ${stepFadeOutMs}ms ease-in, visibility 0s linear ${stepFadeOutMs}ms`,
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  transform: 'none',
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <item.Icon
                  size={20}
                  sx={{
                    flexShrink: 0,
                    ...getStepGlintAnimationSx(
                      householdStepIconGlint,
                      isCurrent
                    ),
                  }}
                />
                <Typography
                  variant="body-s"
                  sx={{
                    fontWeight: FONT_WEIGHTS.regular,
                    overflowWrap: 'anywhere',
                    // The gradient fills the text. The background color fills
                    // the part of the label that the gradient does not cover
                    // at the start and end of each pass.
                    color: 'transparent',
                    backgroundColor: COLORS.TEXT.PRIMARY,
                    backgroundImage: `linear-gradient(90deg, ${COLORS.TEXT.PRIMARY} 38%, ${COLORS.BRAND.ORANGE} 50%, ${COLORS.TEXT.PRIMARY} 62%)`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: stepGlintBackgroundSize,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    ...getStepGlintAnimationSx(householdStepGlint, isCurrent),
                    // Forced-colors mode is an OS accessibility setting, for
                    // example Windows High Contrast. In this mode the browser
                    // replaces page colors with a small user-selected palette
                    // and removes background images, including gradients. The
                    // glint draws the text with a clipped gradient on
                    // transparent text, so without this override the label is
                    // blank in this mode. `CanvasText` is the system color for
                    // text in the user's palette.
                    '@media (forced-colors: active)': {
                      color: 'CanvasText',
                      backgroundImage: 'none',
                    },
                  }}
                >
                  {/* Only the visible label gets the ellipsis, so screen
                      readers announce the plain step text. */}
                  {`${getLabel(item)}…`}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
